import type {
  ListUpstreamModelCostsResponse,
  UpsertUpstreamModelCostEntry,
  UpsertUpstreamModelCostsResponse,
  UpstreamModelCostRecord,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * Procurement multipliers at (upstream channel × physical model) grain —
 * the upstream page's only economics write (epic #1327 PR-5).
 *
 * The identity written here is deliberately NOT a marketplace SKU: a
 * channel buys a model once, so every SKU routed onto that model shares
 * one price. Reintroducing a per-SKU field on this surface would create a
 * second writer for a value the SKU detail page only reads (#1329).
 *
 * The PUT carries an `Idempotency-Key`. Both ops-api and the gateway fall
 * back to a server-minted id when the header is absent, which makes a
 * network-error retry a *different* operation — so the caller mints one
 * per edit and replays it verbatim, and a committed retry returns the
 * original receipt instead of writing twice.
 */

const BASE_PATH = '/v1/ops/upstreams';

export function upstreamModelCostsQueryKey(
  upstreamId: string,
): readonly ['upstreams', string, 'model-costs'] {
  return ['upstreams', upstreamId, 'model-costs'] as const;
}

export async function listUpstreamModelCosts(
  upstreamId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<UpstreamModelCostRecord[]> {
  const response = await opsApiRequest<ListUpstreamModelCostsResponse>(
    `${BASE_PATH}/${encodeURIComponent(upstreamId)}/model-costs`,
    { signal },
    fetchImpl,
  );
  return [...response.costs];
}

export async function setUpstreamModelCosts(
  upstreamId: string,
  costs: readonly UpsertUpstreamModelCostEntry[],
  operationId: string,
  fetchImpl: typeof fetch,
): Promise<UpsertUpstreamModelCostsResponse> {
  return opsApiRequest<UpsertUpstreamModelCostsResponse>(
    `${BASE_PATH}/${encodeURIComponent(upstreamId)}/model-costs`,
    {
      method: 'PUT',
      body: { costs },
      headers: { 'Idempotency-Key': operationId },
    },
    fetchImpl,
  );
}

/**
 * Operation id for one model-cost PUT. Matches the id grammar both
 * ops-api and the gateway validate (`[A-Za-z0-9][A-Za-z0-9_.:-]{0,127}`);
 * the non-crypto branch exists for the happy-dom test environment.
 */
export function createModelCostOperationId(): string {
  if (globalThis.crypto !== undefined && 'randomUUID' in globalThis.crypto) {
    return `model-cost-${globalThis.crypto.randomUUID()}`;
  }
  return `model-cost-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useUpstreamModelCosts(
  upstreamId: string,
): UseQueryResult<UpstreamModelCostRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: upstreamModelCostsQueryKey(upstreamId),
    queryFn: ({ signal }) => listUpstreamModelCosts(upstreamId, fetchImpl, signal),
    enabled: upstreamId.length > 0,
  });
}

export interface SetUpstreamModelCostsInput {
  readonly costs: readonly UpsertUpstreamModelCostEntry[];
  /** Minted once per edit and replayed verbatim on retry. */
  readonly operationId: string;
}

export function useSetUpstreamModelCosts(
  upstreamId: string,
): UseMutationResult<UpsertUpstreamModelCostsResponse, Error, SetUpstreamModelCostsInput> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ costs, operationId }) =>
      setUpstreamModelCosts(upstreamId, costs, operationId, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: upstreamModelCostsQueryKey(upstreamId) });
      // The marketplace list prices its rows off the same (channel, model)
      // identity through its own cached slice (`skuProcurementCosts.ts`),
      // which lives under this prefix precisely so an upstream-side edit
      // reaches it.
      void qc.invalidateQueries({ queryKey: ['upstreams', upstreamId, 'procurement-cost-index'] });
    },
  });
}
